import {
  Component,
  OnDestroy,
  Input,
  ViewEncapsulation,
} from "@angular/core";
import {
  FormGroup,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";

import { Subscription, Observable, zip } from "rxjs";
import { skip } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  getPosTypesList,
  getPropertiesForListByUsage,
  getSiteFuelPointList,
  getSiteMultimediaList,
  getSitePeripheralList,
  getSitePosList,
} from "src/app/store/actions/sites/sites.actions";
import {
  AppState,
  sitePosLoadedState,
  siteFuelPointLoadedState,
  sitePosTypesLoadedState,
  loadedDevicePosPropertiesListSiteState,
  sitePeripheralLoadedState,
  siteMultimediaLoadedState,
} from "src/app/store/reducers";

import {
  PosType,
  SitePosDTO,
} from "src/app/models/Sites/sitePosDTO";
import { SiteFuelPointDTO } from "src/app/models/Sites/siteFuelPointDTO";
import { SitePeripheralDTO } from "src/app/models/Sites/sitePeripheralDTO";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import { SiteMultimediaDTO } from "src/app/models/Sites/siteMultimediaDTO";

import { PosFormDialogComponent } from "./dialog/pos-form-dialog.component";

const USAGE_CODE = "POS";

@Component({
  selector: "site-pos-details",
  templateUrl: "./pos-details.component.html",
  styleUrls: ["./pos-details.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class PosDetailsComponent implements OnDestroy {
  _siteCode: string;

  @Input() set siteCode(value: string) {
    this._siteCode = value;
    if (this._siteCode) {
      this.loadPos(this._siteCode);
      this.loadFuelPoints(this._siteCode);
      this.loadPeripherals(this._siteCode);
      this.loadMultimediaDevices(this._siteCode);
      this.loadPosTypes();
    }
  }

  @Input() permissionResource: string;

  multiSubs: Subscription;
  posSub: Subscription;
  posList$: Observable<SitePosDTO[]>;
  posList: SitePosDTO[];

  //Site Fuel Points List
  fuelPointList$: Observable<SiteFuelPointDTO[]>;
  fuelPointList: SiteFuelPointDTO[];
  fuelPointSub: Subscription;

  //Site Peripherals List
  peripheralList$: Observable<SitePeripheralDTO[]>;
  peripheralList: SitePeripheralDTO[];
  peripheralSub: Subscription;

  //Site Multimedia Devices List
  multimediaList$: Observable<SiteMultimediaDTO[]>;
  multimediaList: SiteMultimediaDTO[];
  multimediaSub: Subscription;

  //Pos Types list
  posTypeList$: Observable<PosType[]>;
  posTypeList: PosType[];

  //Property List
  propertyList$: Observable<PropertyDTO[]>;
  propertyList: PropertyDTO[];

  currentPos: SitePosDTO;
  isPosSelected = false;

  posDataForm: FormGroup;

  constructor(
    private store: Store<AppState>,
    public dialog: MatDialog
  ) {
    this.posList$ = this.store.pipe(select(sitePosLoadedState));
    this.posTypeList$ = this.store.pipe(select(sitePosTypesLoadedState));
    this.fuelPointList$ = this.store.pipe(select(siteFuelPointLoadedState));
    this.peripheralList$ = this.store.pipe(select(sitePeripheralLoadedState));
    this.multimediaList$ = this.store.pipe(select(siteMultimediaLoadedState));
    this.propertyList$ = this.store.pipe(select(loadedDevicePosPropertiesListSiteState));
  }

  loadPos(code: string) {
    if (code) {
      this.posSub = this.posList$.subscribe((posList: SitePosDTO[]) => {
        this.posList = posList;
        if (posList && posList.length && this.currentPos) {
          this.currentPos = this.posList.find(({ code: posCode }) => posCode === this.currentPos.code);
        }
        this.resetPos();
      });
      this.store.dispatch(getSitePosList({ 
        code,
        pageReq: { realSize: true, offset: 0, limit: 999 },
      }));
    }
  }

  loadPosTypes() {
    this.multiSubs = zip(
      this.posTypeList$.pipe(skip(1)),
      this.propertyList$.pipe(skip(1))
    ).subscribe(([posTypes, properties]) => {
      if (posTypes && posTypes.length) {
        this.posTypeList = posTypes;
      }
      if (properties && properties.length) {
        this.propertyList = properties;
      }
    });
    this.store.dispatch(getPosTypesList(null));
    this.store.dispatch(
      getPropertiesForListByUsage({
        pageReq: {
          query: "is_enabled==1",
          limit: undefined,
          offset: 0,
          realSize: true,
        },
        usageCode: USAGE_CODE,
      })
    );
  }

  loadFuelPoints(code: string) {
    this.fuelPointSub = this.fuelPointList$.subscribe(
      (fuelPoints: SiteFuelPointDTO[]) => {
        this.fuelPointList = fuelPoints;
      }
    );

    this.store.dispatch(getSiteFuelPointList({ 
      code,
      pageReq: { realSize: true, offset: 0, limit: 999 },
    }));
  }

  loadPeripherals(code: string) {
    this.peripheralSub = this.peripheralList$.subscribe(
      (peripherals: SitePeripheralDTO[]) => {
        this.peripheralList = peripherals;
      }
    );
    this.store.dispatch(getSitePeripheralList({
      code,
      pageReq: { realSize: true, offset: 0, limit: 999 },
    }))
  }

  loadMultimediaDevices(code: string) {
    this.multimediaSub = this.multimediaList$.subscribe(
      (multimediaDevices: SiteMultimediaDTO[]) => {
        this.multimediaList = multimediaDevices;
      }
    );
    this.store.dispatch(getSiteMultimediaList({
      code,
      pageReq: { realSize: true, offset: 0, limit: 999 },
    }))
  }


  resetPos() {
    this.dialog.closeAll();
    this.isPosSelected = false;
    this.currentPos = null;
  }

  selectPos(pos: SitePosDTO) {
    this.isPosSelected = true;
    this.currentPos = pos;

    this.openPosFormDialog(false);
  }

  create() {
    this.isPosSelected = false;
    this.currentPos = null;

    this.openPosFormDialog(true);
  }

  openPosFormDialog(isCreate: boolean) {
    this.dialog.open(PosFormDialogComponent, {
      autoFocus: false,
      data: {
        isCreate,
        siteCode: this._siteCode,
        currentPos: this.currentPos,
        fuelPointList: this.fuelPointList,
        peripheralList: this.peripheralList,
        multimediaList: this.multimediaList,
        posTypeList: this.posTypeList,
        propertyList: this.propertyList,
      }
    });
  }

  ngOnDestroy(): void {
    if (this.posSub) {
      this.posSub.unsubscribe();
    }
    if (this.fuelPointSub) {
      this.fuelPointSub.unsubscribe();
    }
    if (this.peripheralSub) {
      this.peripheralSub.unsubscribe();
    }
    if (this.multimediaSub) {
      this.multimediaSub.unsubscribe();
    } 
    if (this.multiSubs) {
      this.multiSubs.unsubscribe();
    }
  }
}
